// Onboarding Page - First-time user experience

import { initDatabase, importDatabaseFile, query } from "../db.js";
import { navigateTo, showToast } from "../router.js";

// Load user settings into app state
function loadSettings() {
  const settings = query("SELECT * FROM UserSettings LIMIT 1");

  if (settings.length > 0) {
    window.appState.settings = settings[0];
  } else {
    window.appState.settings = {
      name: "User"
    };
  }
}

export async function renderOnboardingPage() {
  const app = document.getElementById("app");

  app.innerHTML = `
    <div class="onboarding-page" style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: var(--spacing-xl);">
      <div style="max-width: 500px; width: 100%; text-align: center;">
        <!-- Logo/Title -->
        <div style="margin-bottom: var(--spacing-xl);">
          <h1 class="font-display" style="font-size: 3rem; margin-bottom: var(--spacing-sm);">
            Gymnotes
          </h1>
          <p class="text-lg text-muted">
            Welcome! Let's get you started.
          </p>
        </div>

        <!-- Onboarding Options -->
        <div style="display: grid; gap: var(--spacing-md); margin-bottom: var(--spacing-lg);">
          <!-- Start Fresh Option -->
          <button class="onboarding-option" id="start-fresh-btn" style="
            display: flex;
            align-items: center;
            gap: var(--spacing-md);
            padding: var(--spacing-lg);
            background: var(--color-surface);
            border: 2px solid var(--color-border);
            border-radius: var(--radius-lg);
            cursor: pointer;
            transition: all 0.2s;
            text-align: left;
          ">
            <div style="
              flex-shrink: 0;
              width: 60px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: var(--color-primary);
              border-radius: var(--radius-md);
              font-size: 2rem;
            ">
              🏋️
            </div>
            <div style="flex: 1;">
              <h2 style="font-weight: 600; margin-bottom: var(--spacing-xs);">
                Start Fresh
              </h2>
              <p class="text-sm text-muted">
                Begin with pre-loaded exercises and an empty workout history
              </p>
            </div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          <!-- Import Database Option -->
          <button class="onboarding-option" id="import-database-btn" style="
            display: flex;
            align-items: center;
            gap: var(--spacing-md);
            padding: var(--spacing-lg);
            background: var(--color-surface);
            border: 2px solid var(--color-border);
            border-radius: var(--radius-lg);
            cursor: pointer;
            transition: all 0.2s;
            text-align: left;
          ">
            <div style="
              flex-shrink: 0;
              width: 60px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: var(--color-secondary);
              border-radius: var(--radius-md);
              font-size: 2rem;
            ">
              📁
            </div>
            <div style="flex: 1;">
              <h2 style="font-weight: 600; margin-bottom: var(--spacing-xs);">
                Import Database
              </h2>
              <p class="text-sm text-muted">
                Load an existing database from a backup file
              </p>
            </div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <!-- Info Text -->
        <p class="text-xs text-muted">
          You can always import or export your database later from Settings
        </p>
      </div>
    </div>

    <style>
      .onboarding-option:hover {
        border-color: var(--color-primary);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .onboarding-option:active {
        transform: translateY(0);
      }
    </style>
  `;

  setupOnboardingEventListeners();
}

function setupOnboardingEventListeners() {
  // Start Fresh
  document.getElementById("start-fresh-btn")?.addEventListener("click", async () => {
    const btn = document.getElementById("start-fresh-btn");
    btn.disabled = true;
    btn.style.opacity = "0.6";
    btn.style.cursor = "not-allowed";

    try {
      showToast("Creating fresh database...", "info");
      const db = await initDatabase();

      // Set global state
      window.appState.db = db;
      loadSettings();

      showToast("Database created successfully!", "success");

      // Navigate to home
      setTimeout(() => {
        navigateTo("/");
      }, 500);
    } catch (err) {
      console.error("Failed to initialize database:", err);
      showToast("Failed to create database", "error");
      btn.disabled = false;
      btn.style.opacity = "1";
      btn.style.cursor = "pointer";
    }
  });

  // Import Database
  document.getElementById("import-database-btn")?.addEventListener("click", async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".db";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const btn = document.getElementById("import-database-btn");
        btn.disabled = true;
        btn.style.opacity = "0.6";
        btn.style.cursor = "not-allowed";

        try {
          showToast("Importing database...", "info");
          const db = await importDatabaseFile(file);

          // Set global state
          window.appState.db = db;
          loadSettings();

          showToast("Database imported successfully!", "success");

          // Navigate to home
          setTimeout(() => {
            navigateTo("/");
          }, 500);
        } catch (err) {
          console.error("Failed to import database:", err);
          showToast(err.message || "Failed to import database", "error");
          btn.disabled = false;
          btn.style.opacity = "1";
          btn.style.cursor = "pointer";
        }
      }
    };
    input.click();
  });
}
